import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import {
  Building2, Plus, FileText, AlertTriangle,
  Shield, Phone, Mail, User, UserCheck, HardHat,
  Activity, RefreshCw, MapPin, LogOut, ChevronRight, X,
  CheckCircle, Clock, AlertCircle, TrendingUp, TrendingDown,
  Target, Wrench, ArrowLeft
} from 'lucide-react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import CorrectiveActionMetrics from '@/components/CorrectiveActionMetrics';
import useFeatureFlags from '@/hooks/useFeatureFlags';

// Body silhouette with front and back view
const BodySilhouette = ({ injuries = [], size = 'medium' }) => {
  const dimensions = size === 'large' 
    ? { width: 250, height: 225 } 
    : size === 'medium'
    ? { width: 180, height: 162 }
    : { width: 100, height: 90 };
  
  const getInjuryColor = (type) => {
    const t = (type || '').toLowerCase();
    if (t.includes('first') || t === 'firstaid') return '#22c55e';
    if (t.includes('medical')) return '#eab308';
    if (t.includes('recordable')) return '#ef4444';
    return '#6b7280';
  };
  
  return (
    <div className="relative" style={{ width: dimensions.width, height: dimensions.height }}>
      <img 
        src="https://customer-assets.emergentagent.com/job_formbuilder-7/artifacts/dj7vgm3e_injury%20silouette.png" 
        alt="Human body silhouette - front and back"
        className="w-full h-full object-contain pointer-events-none"
        style={{ filter: 'contrast(1.1)', mixBlendMode: 'multiply' }}
        draggable={false}
      />
      {injuries.map((injury, index) => {
        let x = parseFloat(injury.x) || 50;
        let y = parseFloat(injury.y) || 50;
        if (typeof injury.x === 'string' && injury.x.includes('%')) x = parseFloat(injury.x);
        else if (x > 100) x = (x / 500) * 100;
        if (typeof injury.y === 'string' && injury.y.includes('%')) y = parseFloat(injury.y);
        else if (y > 100) y = (y / 450) * 100;
        
        return (
          <div
            key={index}
            className="absolute rounded-full"
            style={{
              left: `${x}%`,
              top: `${y}%`,
              width: size === 'large' ? 12 : 8,
              height: size === 'large' ? 12 : 8,
              backgroundColor: getInjuryColor(injury.type),
              transform: 'translate(-50%, -50%)',
              boxShadow: `0 0 6px ${getInjuryColor(injury.type)}`,
              border: '2px solid white'
            }}
          />
        );
      })}
    </div>
  );
};

// Tile colors for subcontractors
const TILE_COLORS = [
  { bg: 'bg-gradient-to-br from-red-50 to-red-100', border: 'border-red-200', icon: 'text-red-600' },
  { bg: 'bg-gradient-to-br from-blue-50 to-blue-100', border: 'border-blue-200', icon: 'text-blue-600' },
  { bg: 'bg-gradient-to-br from-green-50 to-green-100', border: 'border-green-200', icon: 'text-green-600' },
  { bg: 'bg-gradient-to-br from-yellow-50 to-yellow-100', border: 'border-yellow-200', icon: 'text-yellow-600' },
  { bg: 'bg-gradient-to-br from-purple-50 to-purple-100', border: 'border-purple-200', icon: 'text-purple-600' },
  { bg: 'bg-gradient-to-br from-pink-50 to-pink-100', border: 'border-pink-200', icon: 'text-pink-600' },
  { bg: 'bg-gradient-to-br from-orange-50 to-orange-100', border: 'border-orange-200', icon: 'text-orange-600' },
  { bg: 'bg-gradient-to-br from-teal-50 to-teal-100', border: 'border-teal-200', icon: 'text-teal-600' },
];

// Main GC Dashboard Component
const GCDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const featureFlags = useFeatureFlags();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', description: '', location: '', startDate: '', endDate: '' });
  
  // Detail view states
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [detailType, setDetailType] = useState(null); // 'overview' or 'sub'
  const [selectedSub, setSelectedSub] = useState(null);

  // Invite subcontractor states
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [availableSubs, setAvailableSubs] = useState([]);
  const [selectedSubToInvite, setSelectedSubToInvite] = useState('');
  const [loadingAvailableSubs, setLoadingAvailableSubs] = useState(false);
  const [inviting, setInviting] = useState(false);

  const fetchAvailableSubs = async (projectId) => {
    setLoadingAvailableSubs(true);
    try {
      const response = await axiosInstance.get(`/gc/projects/${projectId}/available-subcontractors`);
      setAvailableSubs(response.data || []);
    } catch (error) {
      console.error('Error fetching available subs:', error);
      toast.error('Failed to load available subcontractors');
    } finally {
      setLoadingAvailableSubs(false);
    }
  };

  const openInviteDialog = () => {
    if (!selectedProjectId) {
      toast.error('Select a project first');
      return;
    }
    setSelectedSubToInvite('');
    fetchAvailableSubs(selectedProjectId);
    setShowInviteDialog(true);
  };

  const handleInviteExisting = async () => {
    if (!selectedSubToInvite) {
      toast.error('Please select a subcontractor to invite');
      return;
    }
    setInviting(true);
    try {
      await axiosInstance.post(`/gc/projects/${selectedProjectId}/invite`, {
        subCompanyId: selectedSubToInvite
      });
      toast.success('Subcontractor invited to this project');
      setShowInviteDialog(false);
      fetchDashboardData(selectedProjectId);
    } catch (error) {
      console.error('Invite error:', error);
      toast.error(error.response?.data?.detail || 'Failed to invite subcontractor');
    } finally {
      setInviting(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProjectId) {
      fetchDashboardData(selectedProjectId);
    }
  }, [selectedProjectId]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/gc/projects');
      setProjects(response.data);
      if (response.data.length > 0 && !selectedProjectId) {
        setSelectedProjectId(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardData = async (projectId) => {
    try {
      const response = await axiosInstance.get(`/gc/projects/${projectId}/dashboard-tiles`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load dashboard data');
    }
  };

  const handleCreateProject = async () => {
    if (!newProject.name) {
      toast.error('Project name is required');
      return;
    }
    try {
      const response = await axiosInstance.post('/gc/projects', newProject);
      toast.success('Project created');
      setShowCreateDialog(false);
      setNewProject({ name: '', description: '', location: '', startDate: '', endDate: '' });
      fetchProjects();
      setSelectedProjectId(response.data.id);
    } catch (error) {
      toast.error('Failed to create project');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/login');
  };

  const openProjectOverview = () => {
    setDetailType('overview');
    setSelectedSub(null);
    setShowDetailDialog(true);
  };

  const openSubDetails = (sub) => {
    setDetailType('sub');
    setSelectedSub(sub);
    setShowDetailDialog(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {user?.role !== 'gc_admin' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin')}
                data-testid="gc-dashboard-back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center text-white font-bold">
                SM
              </div>
              <div>
                <span className="font-bold text-gray-900">Safety</span>
                <span className="font-bold text-orange-500">Mind</span>
                <span className="text-gray-500 text-sm"> AI</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {projects.length > 0 && (
              <Select value={selectedProjectId || ''} onValueChange={setSelectedProjectId}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Select a project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(project => (
                    <SelectItem key={project.id} value={project.id}>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        {project.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            <Button variant="outline" size="icon" onClick={() => fetchDashboardData(selectedProjectId)}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            
            {selectedProjectId && (
              <Button variant="outline" onClick={() => navigate(`/gc/analytics/${selectedProjectId}`)}>
                <Activity className="w-4 h-4 mr-2" />
                Analytics
              </Button>
            )}
            
            {selectedProjectId && (
              <Button
                variant="outline"
                onClick={openInviteDialog}
                data-testid="gc-invite-sub-header-btn"
              >
                <UserCheck className="w-4 h-4 mr-2" />
                Invite Sub
              </Button>
            )}
            
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              New Project
            </Button>
            
            <Button variant="ghost" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {featureFlags.phase3CorrectiveActions && (
          <div className="mb-6">
            <CorrectiveActionMetrics scope="gc" />
          </div>
        )}

        {/* Parent GC Portfolio Analytics Banner */}
        {user?.isParentGc && (
          <div className="mb-6 bg-gradient-to-r from-purple-600 to-purple-800 rounded-xl p-4 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-white font-semibold text-lg">Parent GC Portfolio Analytics</h2>
                  <p className="text-purple-200 text-sm">
                    View aggregated safety data across all your child GC companies
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/parent-gc/dashboard')}
                className="bg-white text-purple-700 hover:bg-purple-50"
              >
                <Building2 className="w-4 h-4 mr-2" />
                Open Portfolio Dashboard
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Project Title */}
        {dashboardData && (
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900">{dashboardData.projectName}</h1>
            {dashboardData.projectLocation && (
              <p className="text-gray-600 flex items-center gap-1 mt-1">
                <MapPin className="w-4 h-4" />
                {dashboardData.projectLocation}
              </p>
            )}
          </div>
        )}

        {/* No Projects State */}
        {projects.length === 0 && (
          <Card className="text-center py-16">
            <CardContent>
              <Building2 className="w-20 h-20 text-gray-300 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-gray-600 mb-2">No Projects Yet</h2>
              <p className="text-gray-500 mb-6">Create your first project to start managing subcontractors</p>
              <Button size="lg" onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-5 h-5 mr-2" />
                Create Your First Project
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Simple Tile Grid */}
        {dashboardData && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {/* Project Overview Tile */}
            <Card 
              className="bg-gradient-to-br from-indigo-50 to-blue-100 border-indigo-200 cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02]"
              onClick={openProjectOverview}
            >
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
                    <Activity className="w-6 h-6 text-indigo-600" />
                  </div>
                  <ChevronRight className="w-5 h-5 text-indigo-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Project Overview</h3>
                <p className="text-sm text-gray-600 mt-1">All subcontractors combined</p>
              </CardContent>
            </Card>
            
            {/* Subcontractor Tiles - With Quick Stats */}
            {dashboardData.subcontractorTiles?.map((sub, index) => {
              const colors = TILE_COLORS[index % TILE_COLORS.length];
              const analytics = sub.analytics || {};
              const needsAttention = analytics.riskStatus?.level === 'High' || analytics.riskStatus?.level === 'Critical';
              
              return (
                <Card 
                  key={sub.subCompanyId}
                  className={`${colors.bg} ${colors.border} cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02]`}
                  onClick={() => openSubDetails(sub)}
                >
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-3">
                      <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
                        <Building2 className={`w-5 h-5 ${colors.icon}`} />
                      </div>
                      <div className="flex items-center gap-2">
                        {needsAttention && (
                          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                            Needs Attention
                          </span>
                        )}
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 line-clamp-1">{sub.subCompanyName}</h3>
                    <p className="text-xs text-gray-500 mt-0.5 mb-3">Subcontractor</p>
                    
                    {/* Quick Stats Grid */}
                    <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-gray-200/50">
                      <div className="text-center p-2 bg-white/50 rounded-lg">
                        <p className="text-lg font-bold text-blue-600">{analytics.totalJsas || 0}</p>
                        <p className="text-xs text-gray-500">JSAs</p>
                      </div>
                      <div className="text-center p-2 bg-white/50 rounded-lg">
                        <p className="text-lg font-bold text-red-600">{analytics.totalIncidents || 0}</p>
                        <p className="text-xs text-gray-500">Incidents</p>
                      </div>
                      <div className="text-center p-2 bg-white/50 rounded-lg">
                        <p className="text-lg font-bold text-green-600">{analytics.totalAudits || 0}</p>
                        <p className="text-xs text-gray-500">Audits</p>
                      </div>
                      <div className="text-center p-2 bg-white/50 rounded-lg">
                        <p className="text-lg font-bold text-purple-600">
                          {analytics.avgAuditScore > 0 ? `${analytics.avgAuditScore}%` : '-'}
                        </p>
                        <p className="text-xs text-gray-500">Score</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            
            {/* Empty state if no subs — now a working CTA to invite subs */}
            {(!dashboardData.subcontractorTiles || dashboardData.subcontractorTiles.length === 0) && (
              <button
                type="button"
                onClick={openInviteDialog}
                data-testid="gc-invite-sub-empty-state"
                className="w-full text-left"
              >
                <Card className="bg-gray-50 border-dashed border-2 border-gray-300 hover:border-blue-500 hover:bg-blue-50 transition cursor-pointer">
                  <CardContent className="p-6 text-center">
                    <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-700 font-medium">No subcontractors</p>
                    <p className="text-blue-600 text-sm mt-1 underline">Invite subs to this project</p>
                  </CardContent>
                </Card>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Detail Dialog - Shows when clicking a tile */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {detailType === 'overview' ? (
                <>
                  <Activity className="w-5 h-5 text-indigo-600" />
                  Project Overview
                </>
              ) : (
                <>
                  <Building2 className="w-5 h-5 text-blue-600" />
                  {selectedSub?.subCompanyName}
                </>
              )}
            </DialogTitle>
            {detailType === 'sub' && selectedSub?.divisionName && (
              <DialogDescription>{selectedSub.divisionName}</DialogDescription>
            )}
          </DialogHeader>
          
          {/* Project Overview Content */}
          {detailType === 'overview' && dashboardData?.projectOverview && (
            <div className="space-y-6">
              {/* Risk Status Banner */}
              {dashboardData.projectOverview.riskStatus && (
                <div className={`p-4 rounded-lg flex items-center justify-between ${
                  dashboardData.projectOverview.riskStatus.level === 'Critical' ? 'bg-red-100 border border-red-300' :
                  dashboardData.projectOverview.riskStatus.level === 'High' ? 'bg-orange-100 border border-orange-300' :
                  dashboardData.projectOverview.riskStatus.level === 'Medium' ? 'bg-yellow-100 border border-yellow-300' :
                  'bg-green-100 border border-green-300'
                }`}>
                  <div className="flex items-center gap-3">
                    <AlertCircle className={`w-6 h-6 ${
                      dashboardData.projectOverview.riskStatus.level === 'Critical' ? 'text-red-600' :
                      dashboardData.projectOverview.riskStatus.level === 'High' ? 'text-orange-600' :
                      dashboardData.projectOverview.riskStatus.level === 'Medium' ? 'text-yellow-600' :
                      'text-green-600'
                    }`} />
                    <div>
                      <p className="font-semibold">Risk Status: {dashboardData.projectOverview.riskStatus.level}</p>
                      <p className="text-sm text-gray-600">Risk Score: {dashboardData.projectOverview.riskStatus.score}</p>
                    </div>
                  </div>
                  <Badge className={
                    dashboardData.projectOverview.riskStatus.level === 'Critical' ? 'bg-red-600' :
                    dashboardData.projectOverview.riskStatus.level === 'High' ? 'bg-orange-500' :
                    dashboardData.projectOverview.riskStatus.level === 'Medium' ? 'bg-yellow-500' :
                    'bg-green-500'
                  }>
                    {dashboardData.projectOverview.riskStatus.level}
                  </Badge>
                </div>
              )}
              
              {/* Main Stats Row */}
              <div className="grid grid-cols-5 gap-3">
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <FileText className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                  <p className="text-xl font-bold text-blue-700">{dashboardData.projectOverview.totalJsas}</p>
                  <p className="text-xs text-gray-500">JSAs</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg text-center">
                  <AlertTriangle className="w-5 h-5 text-red-600 mx-auto mb-1" />
                  <p className="text-xl font-bold text-red-700">{dashboardData.projectOverview.totalIncidents}</p>
                  <p className="text-xs text-gray-500">Incidents</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <Shield className="w-5 h-5 text-green-600 mx-auto mb-1" />
                  <p className="text-xl font-bold text-green-700">{dashboardData.projectOverview.totalAudits}</p>
                  <p className="text-xs text-gray-500">Audits</p>
                </div>
                <div className="p-3 bg-yellow-50 rounded-lg text-center">
                  <Activity className="w-5 h-5 text-yellow-600 mx-auto mb-1" />
                  <p className="text-xl font-bold text-yellow-700">{dashboardData.projectOverview.totalNearMisses}</p>
                  <p className="text-xs text-gray-500">Near Misses</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg text-center">
                  <Target className="w-5 h-5 text-purple-600 mx-auto mb-1" />
                  <p className="text-xl font-bold text-purple-700">
                    {dashboardData.projectOverview.avgAuditScore > 0 ? `${dashboardData.projectOverview.avgAuditScore}%` : '-'}
                  </p>
                  <p className="text-xs text-gray-500">Avg Audit</p>
                </div>
              </div>
              
              {/* JSA Compliance & Corrective Actions */}
              <div className="grid grid-cols-2 gap-4">
                {/* JSA Compliance */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium">JSA Compliance</p>
                    <span className="text-2xl font-bold text-blue-700">
                      {dashboardData.projectOverview.jsaCompliance || 0}%
                    </span>
                  </div>
                  <Progress value={dashboardData.projectOverview.jsaCompliance || 0} className="h-2" />
                  <p className="text-xs text-gray-500 mt-1">Completed/Approved JSAs</p>
                </div>
                
                {/* Corrective Actions */}
                <div className="p-4 bg-orange-50 rounded-lg">
                  <p className="font-medium mb-2">Corrective Actions</p>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-lg font-bold text-orange-600">
                        {dashboardData.projectOverview.correctiveActions?.open || 0}
                      </p>
                      <p className="text-xs text-gray-500">Open</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-green-600">
                        {dashboardData.projectOverview.correctiveActions?.closed || 0}
                      </p>
                      <p className="text-xs text-gray-500">Closed</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-red-600">
                        {dashboardData.projectOverview.correctiveActions?.overdue || 0}
                      </p>
                      <p className="text-xs text-gray-500">Overdue</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Incidents & Near Misses Breakdown - Stacked Progress Bars */}
              <div className="grid grid-cols-2 gap-6">
                {/* Incidents by Severity */}
                <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">Incidents by Severity</h3>
                  {Object.keys(dashboardData.projectOverview.incidentsBySeverity || {}).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(dashboardData.projectOverview.incidentsBySeverity).map(([sev, count]) => {
                        const total = Object.values(dashboardData.projectOverview.incidentsBySeverity).reduce((a, b) => a + b, 0);
                        const percentage = total > 0 ? (count / total) * 100 : 0;
                        const colorClass = 
                          sev.toLowerCase() === 'critical' ? 'bg-red-500' :
                          sev.toLowerCase() === 'high' ? 'bg-orange-500' :
                          sev.toLowerCase() === 'medium' ? 'bg-yellow-500' : 'bg-green-500';
                        return (
                          <div key={sev} className="flex items-center gap-3">
                            <span className="text-xs text-gray-500 w-16 capitalize">{sev}</span>
                            <div className="flex-1 bg-gray-100 rounded-full h-3">
                              <div className={`${colorClass} h-3 rounded-full transition-all duration-500`} style={{width: `${Math.max(percentage, 5)}%`}}></div>
                            </div>
                            <span className="text-sm font-semibold w-8 text-right">{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic">No incidents recorded</p>
                  )}
                </div>
                
                {/* Near Misses by Category */}
                <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">Near Misses by Category</h3>
                  {Object.keys(dashboardData.projectOverview.nearMissesByCategory || {}).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(dashboardData.projectOverview.nearMissesByCategory).map(([cat, count], idx) => {
                        const total = Object.values(dashboardData.projectOverview.nearMissesByCategory).reduce((a, b) => a + b, 0);
                        const percentage = total > 0 ? (count / total) * 100 : 0;
                        const colors = ['bg-purple-500', 'bg-blue-500', 'bg-teal-500', 'bg-indigo-500', 'bg-pink-500'];
                        return (
                          <div key={cat} className="flex items-center gap-3">
                            <span className="text-xs text-gray-500 w-24 truncate" title={cat}>{cat}</span>
                            <div className="flex-1 bg-gray-100 rounded-full h-3">
                              <div className={`${colors[idx % colors.length]} h-3 rounded-full transition-all duration-500`} style={{width: `${Math.max(percentage, 5)}%`}}></div>
                            </div>
                            <span className="text-sm font-semibold w-8 text-right">{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic">No near misses recorded</p>
                  )}
                </div>
              </div>
              
              {/* Body Map */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium mb-4">Project Injury Body Map</p>
                <div className="flex items-center justify-center gap-8">
                  <BodySilhouette injuries={dashboardData.projectOverview.bodyMap?.injuries || []} size="large" />
                  <div className="space-y-3">
                    <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                      <p className="text-3xl font-bold text-gray-700">{dashboardData.projectOverview.bodyMap?.total || 0}</p>
                      <p className="text-sm text-gray-500">Total Injuries</p>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-4 p-2 bg-green-50 rounded">
                        <span className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-green-500" />
                          First Aid
                        </span>
                        <span className="font-bold text-green-700">{dashboardData.projectOverview.bodyMap?.byType?.firstAid || 0}</span>
                      </div>
                      <div className="flex items-center justify-between gap-4 p-2 bg-yellow-50 rounded">
                        <span className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-yellow-500" />
                          Medical
                        </span>
                        <span className="font-bold text-yellow-700">{dashboardData.projectOverview.bodyMap?.byType?.medical || 0}</span>
                      </div>
                      <div className="flex items-center justify-between gap-4 p-2 bg-red-50 rounded">
                        <span className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-red-500" />
                          Recordable
                        </span>
                        <span className="font-bold text-red-700">{dashboardData.projectOverview.bodyMap?.byType?.recordable || 0}</span>
                      </div>
                    </div>
                    <div className="flex justify-around text-xs text-gray-400 pt-2">
                      <span>← FRONT</span>
                      <span>BACK →</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Subcontractor Count */}
              <div className="p-4 bg-indigo-50 rounded-lg flex items-center gap-4">
                <Building2 className="w-10 h-10 text-indigo-600" />
                <div>
                  <p className="text-2xl font-bold text-indigo-700">{dashboardData.subcontractorTiles?.length || 0}</p>
                  <p className="text-gray-600">Active Subcontractors on this project</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Subcontractor Detail Content */}
          {detailType === 'sub' && selectedSub && (
            <div className="space-y-6">
              {/* Risk Status Banner */}
              {selectedSub.analytics?.riskStatus && (
                <div className={`p-4 rounded-lg flex items-center justify-between ${
                  selectedSub.analytics.riskStatus.level === 'Critical' ? 'bg-red-100 border border-red-300' :
                  selectedSub.analytics.riskStatus.level === 'High' ? 'bg-orange-100 border border-orange-300' :
                  selectedSub.analytics.riskStatus.level === 'Medium' ? 'bg-yellow-100 border border-yellow-300' :
                  'bg-green-100 border border-green-300'
                }`}>
                  <div className="flex items-center gap-3">
                    <AlertCircle className={`w-6 h-6 ${
                      selectedSub.analytics.riskStatus.level === 'Critical' ? 'text-red-600' :
                      selectedSub.analytics.riskStatus.level === 'High' ? 'text-orange-600' :
                      selectedSub.analytics.riskStatus.level === 'Medium' ? 'text-yellow-600' :
                      'text-green-600'
                    }`} />
                    <div>
                      <p className="font-semibold">Risk Status: {selectedSub.analytics.riskStatus.level}</p>
                      <p className="text-sm text-gray-600">Risk Score: {selectedSub.analytics.riskStatus.score}</p>
                    </div>
                  </div>
                  <Badge className={
                    selectedSub.analytics.riskStatus.level === 'Critical' ? 'bg-red-600' :
                    selectedSub.analytics.riskStatus.level === 'High' ? 'bg-orange-500' :
                    selectedSub.analytics.riskStatus.level === 'Medium' ? 'bg-yellow-500' :
                    'bg-green-500'
                  }>
                    {selectedSub.analytics.riskStatus.level}
                  </Badge>
                </div>
              )}
              
              {/* Main Stats */}
              <div className="grid grid-cols-5 gap-3">
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <FileText className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                  <p className="text-xl font-bold text-blue-700">{selectedSub.analytics.totalJsas}</p>
                  <p className="text-xs text-gray-500">JSAs</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg text-center">
                  <AlertTriangle className="w-5 h-5 text-red-600 mx-auto mb-1" />
                  <p className="text-xl font-bold text-red-700">{selectedSub.analytics.totalIncidents}</p>
                  <p className="text-xs text-gray-500">Incidents</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <Shield className="w-5 h-5 text-green-600 mx-auto mb-1" />
                  <p className="text-xl font-bold text-green-700">{selectedSub.analytics.totalAudits}</p>
                  <p className="text-xs text-gray-500">Audits</p>
                </div>
                <div className="p-3 bg-yellow-50 rounded-lg text-center">
                  <Activity className="w-5 h-5 text-yellow-600 mx-auto mb-1" />
                  <p className="text-xl font-bold text-yellow-700">{selectedSub.analytics.totalNearMisses}</p>
                  <p className="text-xs text-gray-500">Near Misses</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg text-center">
                  <Target className="w-5 h-5 text-purple-600 mx-auto mb-1" />
                  <p className="text-xl font-bold text-purple-700">
                    {selectedSub.analytics.avgAuditScore > 0 ? `${selectedSub.analytics.avgAuditScore}%` : '-'}
                  </p>
                  <p className="text-xs text-gray-500">Avg Audit</p>
                </div>
              </div>
              
              {/* JSA Compliance & Corrective Actions */}
              <div className="grid grid-cols-2 gap-4">
                {/* JSA Compliance */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium">JSA Compliance</p>
                    <span className="text-2xl font-bold text-blue-700">
                      {selectedSub.analytics.jsaCompliance || 0}%
                    </span>
                  </div>
                  <Progress value={selectedSub.analytics.jsaCompliance || 0} className="h-2" />
                  <p className="text-xs text-gray-500 mt-1">Completed/Approved JSAs</p>
                </div>
                
                {/* Corrective Actions */}
                <div className="p-4 bg-orange-50 rounded-lg">
                  <p className="font-medium mb-2">Corrective Actions</p>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-lg font-bold text-orange-600">
                        {selectedSub.analytics.correctiveActions?.open || 0}
                      </p>
                      <p className="text-xs text-gray-500">Open</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-green-600">
                        {selectedSub.analytics.correctiveActions?.closed || 0}
                      </p>
                      <p className="text-xs text-gray-500">Closed</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-red-600">
                        {selectedSub.analytics.correctiveActions?.overdue || 0}
                      </p>
                      <p className="text-xs text-gray-500">Overdue</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Incidents & Near Misses Breakdown - Stacked Progress Bars */}
              <div className="grid grid-cols-2 gap-6">
                {/* Incidents by Severity */}
                <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">Incidents by Severity</h3>
                  {Object.keys(selectedSub.analytics.incidentsBySeverity || {}).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(selectedSub.analytics.incidentsBySeverity).map(([sev, count]) => {
                        const total = Object.values(selectedSub.analytics.incidentsBySeverity).reduce((a, b) => a + b, 0);
                        const percentage = total > 0 ? (count / total) * 100 : 0;
                        const colorClass = 
                          sev.toLowerCase() === 'critical' ? 'bg-red-500' :
                          sev.toLowerCase() === 'high' ? 'bg-orange-500' :
                          sev.toLowerCase() === 'medium' ? 'bg-yellow-500' : 'bg-green-500';
                        return (
                          <div key={sev} className="flex items-center gap-3">
                            <span className="text-xs text-gray-500 w-16 capitalize">{sev}</span>
                            <div className="flex-1 bg-gray-100 rounded-full h-3">
                              <div className={`${colorClass} h-3 rounded-full transition-all duration-500`} style={{width: `${Math.max(percentage, 5)}%`}}></div>
                            </div>
                            <span className="text-sm font-semibold w-8 text-right">{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic">No incidents recorded</p>
                  )}
                </div>
                
                {/* Near Misses by Category */}
                <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">Near Misses by Category</h3>
                  {Object.keys(selectedSub.analytics.nearMissesByCategory || {}).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(selectedSub.analytics.nearMissesByCategory).map(([cat, count], idx) => {
                        const total = Object.values(selectedSub.analytics.nearMissesByCategory).reduce((a, b) => a + b, 0);
                        const percentage = total > 0 ? (count / total) * 100 : 0;
                        const colors = ['bg-purple-500', 'bg-blue-500', 'bg-teal-500', 'bg-indigo-500', 'bg-pink-500'];
                        return (
                          <div key={cat} className="flex items-center gap-3">
                            <span className="text-xs text-gray-500 w-24 truncate" title={cat}>{cat}</span>
                            <div className="flex-1 bg-gray-100 rounded-full h-3">
                              <div className={`${colors[idx % colors.length]} h-3 rounded-full transition-all duration-500`} style={{width: `${Math.max(percentage, 5)}%`}}></div>
                            </div>
                            <span className="text-sm font-semibold w-8 text-right">{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic">No near misses recorded</p>
                  )}
                </div>
              </div>
              
              {/* Body Map */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium mb-4">Injury Body Map</p>
                <div className="flex items-center justify-center gap-8">
                  <BodySilhouette injuries={selectedSub.bodyMap?.injuries || []} size="large" />
                  <div className="space-y-3">
                    <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                      <p className="text-3xl font-bold text-gray-700">{selectedSub.bodyMap?.total || 0}</p>
                      <p className="text-sm text-gray-500">Total Injuries</p>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-4 p-2 bg-green-50 rounded">
                        <span className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-green-500" />
                          First Aid
                        </span>
                        <span className="font-bold text-green-700">{selectedSub.bodyMap?.byType?.firstAid || 0}</span>
                      </div>
                      <div className="flex items-center justify-between gap-4 p-2 bg-yellow-50 rounded">
                        <span className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-yellow-500" />
                          Medical
                        </span>
                        <span className="font-bold text-yellow-700">{selectedSub.bodyMap?.byType?.medical || 0}</span>
                      </div>
                      <div className="flex items-center justify-between gap-4 p-2 bg-red-50 rounded">
                        <span className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-red-500" />
                          Recordable
                        </span>
                        <span className="font-bold text-red-700">{selectedSub.bodyMap?.byType?.recordable || 0}</span>
                      </div>
                    </div>
                    <div className="flex justify-around text-xs text-gray-400 pt-2">
                      <span>← FRONT</span>
                      <span>BACK →</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Contacts */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium mb-4">Contacts</p>
                <div className="grid md:grid-cols-3 gap-4">
                  {selectedSub.contacts?.admin && (
                    <div className="p-4 bg-white rounded-lg shadow-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <User className="w-5 h-5 text-blue-600" />
                        <span className="font-medium">Admin</span>
                      </div>
                      <p className="font-semibold">{selectedSub.contacts.admin.name}</p>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                        <Mail className="w-4 h-4" />
                        <a href={`mailto:${selectedSub.contacts.admin.email}`} className="hover:text-blue-600">
                          {selectedSub.contacts.admin.email}
                        </a>
                      </div>
                      {selectedSub.contacts.admin.phone && (
                        <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                          <Phone className="w-4 h-4" />
                          <a href={`tel:${selectedSub.contacts.admin.phone}`} className="hover:text-blue-600">
                            {selectedSub.contacts.admin.phone}
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {selectedSub.contacts?.supervisor && (
                    <div className="p-4 bg-white rounded-lg shadow-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <UserCheck className="w-5 h-5 text-green-600" />
                        <span className="font-medium">Supervisor</span>
                      </div>
                      <p className="font-semibold">{selectedSub.contacts.supervisor.name}</p>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                        <Mail className="w-4 h-4" />
                        <a href={`mailto:${selectedSub.contacts.supervisor.email}`} className="hover:text-blue-600">
                          {selectedSub.contacts.supervisor.email}
                        </a>
                      </div>
                      {selectedSub.contacts.supervisor.phone && (
                        <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                          <Phone className="w-4 h-4" />
                          <a href={`tel:${selectedSub.contacts.supervisor.phone}`} className="hover:text-blue-600">
                            {selectedSub.contacts.supervisor.phone}
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {selectedSub.contacts?.safetyOfficer && (
                    <div className="p-4 bg-white rounded-lg shadow-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <HardHat className="w-5 h-5 text-orange-600" />
                        <span className="font-medium">Safety Officer</span>
                      </div>
                      <p className="font-semibold">{selectedSub.contacts.safetyOfficer.name}</p>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                        <Mail className="w-4 h-4" />
                        <a href={`mailto:${selectedSub.contacts.safetyOfficer.email}`} className="hover:text-blue-600">
                          {selectedSub.contacts.safetyOfficer.email}
                        </a>
                      </div>
                      {selectedSub.contacts.safetyOfficer.phone && (
                        <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                          <Phone className="w-4 h-4" />
                          <a href={`tel:${selectedSub.contacts.safetyOfficer.phone}`} className="hover:text-blue-600">
                            {selectedSub.contacts.safetyOfficer.phone}
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {!selectedSub.contacts?.admin && !selectedSub.contacts?.supervisor && !selectedSub.contacts?.safetyOfficer && (
                    <p className="text-gray-500 col-span-3 text-center py-4">No contacts available</p>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="flex justify-between items-center">
            <Button 
              variant="default"
              onClick={() => {
                setShowDetailDialog(false);
                navigate(`/gc/analytics/${selectedProjectId}`);
              }}
            >
              <FileText className="w-4 h-4 mr-2" />
              View Full Report
            </Button>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Subcontractor Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent data-testid="gc-invite-sub-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Invite Subcontractor to {dashboardData?.projectName || 'Project'}
            </DialogTitle>
            <DialogDescription>
              Choose a subcontractor company that's already registered on SafetyMind AI. They'll see this project in their dashboard once accepted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {loadingAvailableSubs ? (
              <p className="text-sm text-gray-500 text-center py-6">Loading available subcontractors…</p>
            ) : availableSubs.length === 0 ? (
              <div className="text-center py-6 space-y-3">
                <AlertCircle className="w-10 h-10 text-orange-400 mx-auto" />
                <p className="text-sm text-gray-600 font-medium">No eligible subcontractors to invite.</p>
                <p className="text-xs text-gray-500">
                  Either every known subcontractor is already linked to this project, or no subs have been added to your company yet.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowInviteDialog(false);
                    navigate('/subcontractors');
                  }}
                  data-testid="gc-manage-subs-link"
                >
                  Manage Subcontractors →
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="sub-select">Subcontractor</Label>
                <Select value={selectedSubToInvite} onValueChange={setSelectedSubToInvite}>
                  <SelectTrigger id="sub-select" data-testid="gc-invite-sub-select">
                    <SelectValue placeholder="Choose a subcontractor…" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableSubs.map((sub) => (
                      <SelectItem key={sub.id} value={sub.id} data-testid={`gc-invite-sub-option-${sub.id}`}>
                        {sub.name}
                        {sub.industry ? ` — ${sub.industry}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowInviteDialog(false)}
              data-testid="gc-invite-sub-cancel"
            >
              Cancel
            </Button>
            {availableSubs.length > 0 && (
              <Button
                onClick={handleInviteExisting}
                disabled={inviting || !selectedSubToInvite}
                data-testid="gc-invite-sub-submit"
              >
                {inviting ? 'Sending…' : 'Send Invite'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Project Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
            <DialogDescription>Add a new project to manage subcontractors</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Project Name *</Label>
              <Input
                value={newProject.name}
                onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                placeholder="e.g., Downtown Office Complex"
              />
            </div>
            <div>
              <Label>Location</Label>
              <Input
                value={newProject.location}
                onChange={(e) => setNewProject({ ...newProject, location: e.target.value })}
                placeholder="e.g., Houston, TX"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={newProject.description}
                onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                placeholder="Project description..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={newProject.startDate}
                  onChange={(e) => setNewProject({ ...newProject, startDate: e.target.value })}
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={newProject.endDate}
                  onChange={(e) => setNewProject({ ...newProject, endDate: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateProject}>Create Project</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GCDashboard;
